import React, { useCallback, useEffect, useRef, useState } from "react";

import type { ICohortQueryModel } from "@performyard/models";
import { FormSubscribe, Wizard, WizardStep, useWizard } from "@performyard/py-components";
import {
	DEFAULT_REVIEW_CYCLE_DUE_TIME,
	DEFAULT_REVIEW_START_TIME,
	DEFAULT_TIMEZONE,
} from "@performyard/utils-isomorphic";

import { ExitWizardModal } from "../../../../components/ExitWizardModal";
import { WizardStepWrapper } from "../../../../components/WizardStep";
import type { CycleType } from "./Config";
import { StartDateChangeSync } from "./components/StartDateChangeSync";

import type { AddFormsStepData } from "./steps/AddFormsStep/types";

import { useCompanySettings } from "../../../../api/hooks/defs/company";
import { useCreateReviewCycle } from "../../../../api/hooks/defs/review-cycle";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";

import { getUserSelectionCount, removeUsersFromSelection } from "../../../../api/services/defs/user-selection";

import { STEP_ID as ADD_FORMS_STEP_ID, STEP_TITLE as ADD_FORMS_STEP_TITLE, AddFormsStep } from "./steps/AddFormsStep";
import {
	AUTOMATION_RULES_STEP_ID,
	AUTOMATION_RULES_STEP_TITLE,
	AutomatedSubjectsStep,
} from "./steps/AutomatedSubjectsStep";
import { CONFIRMATION_STEP_ID, CONFIRMATION_STEP_TITLE, ConfirmationStep } from "./steps/ConfirmationStep";
import { buildCreateCyclePayload } from "./steps/ConfirmationStep/buildCreateCyclePayload";
import { CycleSettingsStep, getSettingsDefaults, getSettingsSchema } from "./steps/CycleSettingsStep";
import type { SettingsData } from "./steps/CycleSettingsStep/schema";
import {
	QUALIFYING_SUBJECTS_STEP_ID,
	QUALIFYING_SUBJECTS_STEP_TITLE,
	QualifyingSubjectsStep,
} from "./steps/QualifyingSubjectsStep";
import { SUBJECTS_STEP_ID, SUBJECTS_STEP_TITLE, SelectSubjectsStep } from "./steps/SelectSubjectsStep";

/** Seed values for the Copy flow, keyed by step id. Assignable to the generic Wizard's `initialData`. */
export type ReviewCycleWizardInitialData = Partial<
	Record<"basic-info", SettingsData> &
		Record<typeof ADD_FORMS_STEP_ID, AddFormsStepData> &
		Record<typeof SUBJECTS_STEP_ID, { userSelectionId: string; selectedCount: number }> &
		Record<typeof AUTOMATION_RULES_STEP_ID, { autoflowUserQuery: ICohortQueryModel }>
>;

type ReviewCycleWizardProps = {
	cycleType: CycleType;
	onExit: () => void;
	onCreated?: (cycleId: string) => void;
	/** Seeds the wizard when copying an existing cycle, keyed by step id. */
	initialData?: ReviewCycleWizardInitialData;
};

const GroupChangeSync = ({ groupId, userSelectionId }: { groupId: string | null; userSelectionId: string | null }) => {
	const { resetStepsFrom, setStepData, steps } = useWizard();
	const prevGroupIdRef = useRef(groupId);

	useEffect(() => {
		if (prevGroupIdRef.current !== groupId) {
			prevGroupIdRef.current = groupId;
			const subjectsIndex = steps.findIndex((s) => s.id === SUBJECTS_STEP_ID);
			if (subjectsIndex !== -1) {
				resetStepsFrom(subjectsIndex);
				setStepData(SUBJECTS_STEP_ID, null);
			}
			if (userSelectionId) {
				removeUsersFromSelection({ selectionObjectID: userSelectionId, allUsers: true });
			}
		}
	}, [groupId, resetStepsFrom, setStepData, steps, userSelectionId]);

	return null;
};

const EXIT_MESSAGE = "Your review cycle configuration will not be saved. Are you sure you want to exit?";

const CYCLE_TYPE_LABELS: Record<CycleType, string> = {
	manual: "a Manual",
	automated: "an Automated",
	group: "a Group",
};

export const ReviewCycleWizard = ({ cycleType, onExit, onCreated, initialData }: ReviewCycleWizardProps) => {
	const [showExitConfirm, setShowExitConfirm] = useState(false);
	const [suppressUnsavedChanges, setSuppressUnsavedChanges] = useState(false);
	const { mutateAsync: createReviewCycle, isPending: isCreating, isSuccess: hasCreated } = useCreateReviewCycle();
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges({
		message: "You have unsaved changes. Are you sure you want to leave this page?",
		disabled: suppressUnsavedChanges || isCreating || hasCreated,
	});

	const handleExitRequest = () => {
		setSuppressUnsavedChanges(true);
		setShowExitConfirm(true);
	};

	const handleExitConfirm = () => {
		setIsSafe();
		onExit();
	};

	const handleComplete = async (wizardData: Record<string, unknown>) => {
		const settings = wizardData["basic-info"] as Parameters<typeof buildCreateCyclePayload>[0]["settings"];
		const formsData = wizardData[ADD_FORMS_STEP_ID] as Parameters<typeof buildCreateCyclePayload>[0]["formsData"];
		if (!settings || !formsData) return;

		const payload = buildCreateCyclePayload({
			cycleType,
			settings,
			formsData,
			userSelectionId,
			autoflowUserQuery,
			companyTimezone,
			defaultDueTime: defaultEndTime,
		});
		if (!payload) return;

		const response = await createReviewCycle(payload);
		onCreated?.(response.data.cycleId);
	};

	const typeLabel = CYCLE_TYPE_LABELS[cycleType];
	const stepTitle = `Create ${typeLabel} Review Cycle`;

	const { data: companySettings } = useCompanySettings();
	const companyTimezone = companySettings?.default_timezone ?? DEFAULT_TIMEZONE;
	const reviewSettings = companySettings?.functions?.reviews as
		| { default_review_start_time?: number; default_review_cycle_due_time?: number }
		| undefined;
	const defaultStartTime = reviewSettings?.default_review_start_time ?? DEFAULT_REVIEW_START_TIME;
	const defaultEndTime = reviewSettings?.default_review_cycle_due_time ?? DEFAULT_REVIEW_CYCLE_DUE_TIME;

	const settingsSchema = getSettingsSchema(cycleType, companyTimezone);
	const settingsDefaults = getSettingsDefaults(cycleType);

	const addFormsValidateRef = useRef<(data: unknown) => boolean>(() => true);
	const addFormsBeforeNext = useRef((data: unknown) => addFormsValidateRef.current(data)).current;

	const [userSelectionId, setUserSelectionId] = useState<string | null>(
		initialData?.[SUBJECTS_STEP_ID]?.userSelectionId ?? null
	);
	const [autoflowUserQuery, setAutoflowUserQuery] = useState<ICohortQueryModel | null>(
		initialData?.[AUTOMATION_RULES_STEP_ID]?.autoflowUserQuery ?? null
	);
	const [showSelectionError, setShowSelectionError] = useState(false);

	const subjectsBeforeNext = useCallback(async () => {
		if (!userSelectionId) {
			setShowSelectionError(true);
			return false;
		}
		const data = await getUserSelectionCount(userSelectionId);
		if ((data?.count ?? 0) === 0) {
			setShowSelectionError(true);
			return false;
		}
		setShowSelectionError(false);
		return true;
	}, [userSelectionId]);

	const autoflowBeforeNext = useCallback(async () => {
		if (!autoflowUserQuery || autoflowUserQuery.and.length === 0) {
			return false;
		}
		return true;
	}, [autoflowUserQuery]);

	return (
		<>
			<ExitWizardModal
				isOpen={showExitConfirm}
				message={EXIT_MESSAGE}
				onClose={() => {
					setShowExitConfirm(false);
					setSuppressUnsavedChanges(false);
				}}
				onConfirm={handleExitConfirm}
			/>
			<Wizard
				id="review-cycle-wizard"
				title="Creation Steps"
				submitLabel="Create Review Cycle"
				initialData={initialData}
				onComplete={handleComplete}
				onExit={handleExitRequest}
				onStepChange={() => {
					setIsUnsafe();
					setShowSelectionError(false);
				}}
			>
				<WizardStep
					id="basic-info"
					title="Cycle Settings"
					schema={settingsSchema}
					defaultValues={settingsDefaults}
				>
					{() => (
						<WizardStepWrapper title={stepTitle} subtitle="Cycle Settings">
							<CycleSettingsStep
								cycleType={cycleType}
								companyTimezone={companyTimezone}
								defaultStartTime={defaultStartTime}
							/>
							<FormSubscribe
								id="start-date-change-watcher"
								selector={(state) => (state.values?.startDate as Date | null)?.getTime() ?? null}
							>
								{(startDateMs) => <StartDateChangeSync startDateMs={startDateMs} />}
							</FormSubscribe>
							{cycleType === "group" && (
								<FormSubscribe
									id="group-change-watcher"
									selector={(state) =>
										(state.values?.group as { id: string; name: string } | null)?.id ?? null
									}
								>
									{(groupId) => (
										<GroupChangeSync groupId={groupId} userSelectionId={userSelectionId} />
									)}
								</FormSubscribe>
							)}
						</WizardStepWrapper>
					)}
				</WizardStep>
				<WizardStep id={ADD_FORMS_STEP_ID} title={ADD_FORMS_STEP_TITLE} beforeNext={addFormsBeforeNext}>
					{({ wizardData }) => {
						const settings = wizardData["basic-info"] as
							| { cycleType?: CycleType; startDate?: Date }
							| undefined;
						const cycleStartDate = settings?.startDate ?? new Date();
						return (
							<WizardStepWrapper title={stepTitle} subtitle="Add Forms">
								<AddFormsStep
									cycleStartDate={cycleStartDate}
									companyTimezone={companyTimezone}
									defaultDueTime={defaultEndTime}
									validateRef={addFormsValidateRef}
								/>
							</WizardStepWrapper>
						);
					}}
				</WizardStep>
				{cycleType === "automated"
					? [
							<WizardStep
								key={AUTOMATION_RULES_STEP_ID}
								id={AUTOMATION_RULES_STEP_ID}
								title={AUTOMATION_RULES_STEP_TITLE}
								beforeNext={autoflowBeforeNext}
							>
								{() => (
									<AutomatedSubjectsStep
										stepTitle={stepTitle}
										autoflowUserQuery={autoflowUserQuery}
										onQueryChange={setAutoflowUserQuery}
									/>
								)}
							</WizardStep>,
							<WizardStep
								key={QUALIFYING_SUBJECTS_STEP_ID}
								id={QUALIFYING_SUBJECTS_STEP_ID}
								title={QUALIFYING_SUBJECTS_STEP_TITLE}
							>
								{() => (
									<QualifyingSubjectsStep
										stepTitle={stepTitle}
										autoflowUserQuery={autoflowUserQuery}
									/>
								)}
							</WizardStep>,
						]
					: [
							<WizardStep
								key={SUBJECTS_STEP_ID}
								id={SUBJECTS_STEP_ID}
								title={SUBJECTS_STEP_TITLE}
								beforeNext={subjectsBeforeNext}
							>
								{({ wizardData }) => {
									const settings = wizardData["basic-info"] as
										| { cycleType?: CycleType; group?: { id: string; name: string } | null }
										| undefined;
									const groupId = settings?.group?.id ?? null;
									return (
										<SelectSubjectsStep
											stepTitle={stepTitle}
											cycleType={cycleType}
											groupId={groupId}
											userSelectionId={userSelectionId}
											onSelectionCreated={setUserSelectionId}
											showSelectionError={showSelectionError}
										/>
									);
								}}
							</WizardStep>,
						]}
				<WizardStep id={CONFIRMATION_STEP_ID} title={CONFIRMATION_STEP_TITLE}>
					{({ wizardData }) => (
						<WizardStepWrapper title={stepTitle} subtitle={CONFIRMATION_STEP_TITLE}>
							<ConfirmationStep wizardData={wizardData} cycleType={cycleType} />
						</WizardStepWrapper>
					)}
				</WizardStep>
			</Wizard>
		</>
	);
};
